
import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Settings, Users, BookOpen, BadgeCheck, Briefcase, GraduationCap, CreditCard, ClipboardCheck, Lightbulb, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
  DropdownMenuGroup,
} from "@/components/ui/dropdown-menu";
import { User } from "@supabase/supabase-js";
import useAdminStatus from "@/hooks/useAdminStatus";

interface HeaderProps {
  user: User | null;
  isAuthEnabled: boolean;
  handleSignOut: () => Promise<void>;
}

const Header: React.FC<HeaderProps> = ({ user, isAuthEnabled, handleSignOut }) => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAdmin } = useAdminStatus();

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLoginClick = () => {
    navigate("/login");
  };

  const navLinks = [
    { to: "/advisory", label: "Advisory", icon: Briefcase },
    { to: "/blueprint", label: "Intensive", icon: GraduationCap },
    { to: "/subscription", label: "Continuous", icon: CreditCard },
    { to: "/quiz", label: "Assessment", icon: ClipboardCheck },
    { to: "/insights", label: "Insights", icon: Lightbulb },
    { to: "/contact", label: "Contact", icon: Mail },
  ];

  return (
    <header className="bg-background border-b border-border">
      <div className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            <Link to="/" className="text-xl font-semibold text-techne-purple flex items-center gap-1.5">
              <svg width="28" height="28" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path fillRule="evenodd" clipRule="evenodd" d="M9.92285 0.342773C7.44097 0.342773 5.42285 2.3609 5.42285 4.84277V30.0642C5.42285 32.5461 7.44097 34.5642 9.92285 34.5642H30.5446C33.0265 34.5642 35.0446 32.5461 35.0446 30.0642V23.2189L24.0752 12.2495L23.8352 12.0111L17.5095 5.68534L9.92285 5.68513V0.342773ZM9.92285 11.0851H14.4229V30.0642C14.4229 30.0642 14.4229 30.0642 14.4229 30.0642H9.92285C9.92285 30.0642 9.92285 30.0642 9.92285 30.0642V11.0851ZM18.9229 15.5851H30.5446L30.5446 30.0642C30.5446 30.0642 30.5446 30.0642 30.5446 30.0642H18.9229C18.9229 30.0642 18.9229 30.0642 18.9229 30.0642V15.5851Z" fill="#8B5CF6"/>
                <path fillRule="evenodd" clipRule="evenodd" d="M0 8.62793C0 7.52336 0.89543 6.62793 2 6.62793H7.34311C8.44768 6.62793 9.34311 7.52336 9.34311 8.62793V26.2793C9.34311 27.3839 8.44768 28.2793 7.34311 28.2793H2C0.895431 28.2793 0 27.3839 0 26.2793V8.62793Z" fill="#9B87F5"/>
                <path d="M17.5095 5.68533L20.2341 8.40993L14.4229 14.2211H9.92285V11.0851H14.4229L17.5095 5.68533Z" fill="#9B87F5"/>
                <path d="M23.8352 12.0111L26.5598 14.7357L18.9229 22.3726V15.5851H25.7104L23.8352 12.0111Z" fill="#9B87F5"/>
              </svg>
              <span>BoardSight</span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex lg:items-center lg:space-x-4 xl:space-x-6">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm text-muted-foreground hover:text-foreground transition-colors whitespace-nowrap"
              >
                {link.label}
              </Link>
            ))}
            
          </nav>

          {/* Header Buttons */}
          <div className="flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                    <div className="h-8 w-8 rounded-full bg-techne-purple text-white flex items-center justify-center">
                      {user.user_metadata?.first_name?.charAt(0) || user.email?.charAt(0) || "U"}
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <div className="flex items-center justify-start gap-2 p-2">
                    <div className="flex flex-col space-y-1 leading-none">
                      <p className="font-medium text-sm">
                        {user.user_metadata?.first_name
                          ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ''}`
                          : user.email}
                      </p>
                      {user.email && (
                        <p className="w-[200px] truncate text-sm text-muted-foreground">
                          {user.email}
                        </p>
                      )}
                    </div>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/dashboard">Dashboard</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/courses">My Courses</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/profile">Profile</Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/certificates">My Certificates</Link>
                  </DropdownMenuItem>
                  
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuLabel>Admin</DropdownMenuLabel>
                      <DropdownMenuGroup>
                        <DropdownMenuItem asChild>
                          <Link to="/admin">Admin Dashboard</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/users">User Management</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/courses">Course Management</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/admin/certificates">Certificate Management</Link>
                        </DropdownMenuItem>
                      </DropdownMenuGroup>
                    </>
                  )}
                  
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onSelect={handleSignOut}>
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                {isAuthEnabled && (
                  <button 
                    onClick={handleLoginClick}
                    className="text-sm text-muted-foreground hover:text-foreground"
                  >
                    Log in
                  </button>
                )}
                <Button asChild variant="outline" size="sm">
                  <Link to="/contact#briefing">Book a Briefing</Link>
                </Button>
                <Button asChild size="sm">
                  <Link to="/blueprint#pricing">Enroll</Link>
                </Button>
              </div>
            )}

            <button
              className="ml-2 lg:hidden"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="mt-4 pb-4 lg:hidden">
            <nav className="flex flex-col space-y-3">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                  onClick={toggleMobileMenu}
                >
                  <link.icon size={18} />
                  <span>{link.label}</span>
                </Link>
              ))}
              
              {user && (
                <>
                  <div className="pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground uppercase font-semibold">Account</span>
                  </div>
                  <Link
                    to="/courses"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={toggleMobileMenu}
                  >
                    My Courses
                  </Link>
                  <Link
                    to="/dashboard"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={toggleMobileMenu}
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/certificates"
                    className="text-muted-foreground hover:text-foreground"
                    onClick={toggleMobileMenu}
                  >
                    My Certificates
                  </Link>
                </>
              )}
              
              {isAdmin && (
                <>
                  <div className="pt-2 border-t border-border">
                    <span className="text-xs text-muted-foreground uppercase font-semibold">Admin</span>
                  </div>
                  <Link
                    to="/admin"
                    className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                    onClick={toggleMobileMenu}
                  >
                    <Settings size={18} />
                    <span>Admin Dashboard</span>
                  </Link>
                  <Link
                    to="/admin/users"
                    className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                    onClick={toggleMobileMenu}
                  >
                    <Users size={18} />
                    <span>User Management</span>
                  </Link>
                  <Link
                    to="/admin/courses"
                    className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                    onClick={toggleMobileMenu}
                  >
                    <BookOpen size={18} />
                    <span>Course Management</span>
                  </Link>
                  <Link
                    to="/admin/certificates"
                    className="text-muted-foreground hover:text-foreground flex items-center gap-2"
                    onClick={toggleMobileMenu}
                  >
                    <BadgeCheck size={18} />
                    <span>Certificate Management</span>
                  </Link>
                </>
              )}

              {!user && (
                <div className="pt-4 border-t border-border space-y-3">
                  {isAuthEnabled && (
                    <Button asChild variant="ghost" className="w-full justify-start">
                      <Link to="/login" onClick={toggleMobileMenu}>Log in</Link>
                    </Button>
                  )}
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/contact#briefing" onClick={toggleMobileMenu}>Book a Briefing</Link>
                  </Button>
                  <Button asChild className="w-full">
                    <Link to="/blueprint#pricing" onClick={toggleMobileMenu}>Enroll</Link>
                  </Button>
                </div>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
